import { 
  collection, 
  query, 
  where, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Task, TimeEntry } from '../types/task';
import { getTaskTimeEntries } from './timeTrackingService';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

// pdfMake fontok inicializálása
(pdfMake as any).vfs = pdfFonts;
export interface TaskReportData {
  taskNumber: string;
  title: string;
  status: string;
  priority: string;
  assignedToName?: string;
  estimatedHours?: number;
  actualHours: number;
  completedDate?: Date;
  dueDate?: Date;
}

export type DateFilterType = 'createdAt' | 'completedDate' | 'dueDate' | 'updatedAt' | 'any';

/**
 * Biztonságos dátum konverzió (Firestore Timestamp, Date, string támogatás)
 */
function toDateSafe(value: any): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

/**
 * Ellenőrzi, hogy egy task a megadott hónapban van-e a dátum szűrő beállításai szerint
 */
function isTaskInMonth(
  taskData: any,
  startDate: Date,
  endDate: Date,
  dateFilterType: DateFilterType
): boolean {
  const createdAt = toDateSafe(taskData.createdAt);
  const completedDate = toDateSafe(taskData.completedDate);
  const dueDate = toDateSafe(taskData.dueDate);
  const updatedAt = toDateSafe(taskData.updatedAt);

  switch (dateFilterType) {
    case 'createdAt':
      return createdAt ? (createdAt >= startDate && createdAt <= endDate) : false;
    case 'completedDate':
      return completedDate ? (completedDate >= startDate && completedDate <= endDate) : false;
    case 'dueDate':
      return dueDate ? (dueDate >= startDate && dueDate <= endDate) : false;
    case 'updatedAt':
      return updatedAt ? (updatedAt >= startDate && updatedAt <= endDate) : false;
    case 'any':
    default:
      return !!(
        (createdAt && createdAt >= startDate && createdAt <= endDate) ||
        (completedDate && completedDate >= startDate && completedDate <= endDate) ||
        (dueDate && dueDate >= startDate && dueDate <= endDate) ||
        (updatedAt && updatedAt >= startDate && updatedAt <= endDate)
      );
  }
}

/**
 * Időbejegyzések lekérdezése és task-id szerinti csoportosítás
 * Visszaad egy Map-ot a havi órákkal és egy Set-et arról, mely task-oknak van egyáltalán időbejegyzésük
 */
async function fetchTimeEntryMaps(
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<{
  monthHours: Map<string, number>;
  hasTimeEntriesEver: Set<string>;
}> {
  const timeEntriesQuery = query(
    collection(db, 'timeEntries'),
    where('organizationId', '==', organizationId)
  );

  const snapshot = await getDocs(timeEntriesQuery);
  const monthHours = new Map<string, number>();
  const hasTimeEntriesEver = new Set<string>();

  console.log('[REPORT DEBUG] fetchTimeEntryMaps: total entries fetched:', snapshot.docs.length);

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const taskId = data.taskId as string;
    if (!taskId) continue;

    hasTimeEntriesEver.add(taskId);

    const entryStartTime = toDateSafe(data.startTime);
    if (!entryStartTime) continue;

    // Csak a hónapba eső időbejegyzéseket számoljuk
    if (entryStartTime >= startDate && entryStartTime <= endDate) {
      const duration = (data.duration || 0) as number;
      const hours = duration / 3600;
      monthHours.set(taskId, (monthHours.get(taskId) || 0) + hours);
    }
  }

  console.log('[REPORT DEBUG] fetchTimeEntryMaps result:', {
    tasksWithEntriesEver: hasTimeEntriesEver.size,
    tasksWithEntriesInMonth: monthHours.size,
    monthHours: Object.fromEntries(monthHours.entries())
  });

  return { monthHours, hasTimeEntriesEver };
}

export interface CustomerMonthlyReport {
  customerId: string;
  customerName: string;
  month: string; // YYYY-MM formátum
  tasks: TaskReportData[];
  totalEstimatedHours: number;
  totalActualHours: number;
  completedTasksCount: number;
  totalTasksCount: number;
  generatedAt: Date;
  dateFilterType?: DateFilterType;
}

/**
 * Ügyfél havi kimutatás generálása
 */
export async function generateCustomerMonthlyReport(
  customerId: string,
  customerName: string,
  year: number,
  month: number, // 1-12
  organizationId: string,
  projectId?: string,
  filterByDate: boolean = true,
  statusFilter: string[] = ['todo', 'in_progress', 'completed', 'cancelled'],
  dateFilterType: DateFilterType = 'any'
): Promise<CustomerMonthlyReport> {
  // Hónap első és utolsó napja
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59);

  console.log('[REPORT DEBUG] Generating report for:', JSON.stringify({ customerId, year, month, filterByDate, dateFilterType, statusFilter, projectId }, null, 2));
  console.log('[REPORT DEBUG] Date range:', JSON.stringify({ startDate: startDate.toISOString(), endDate: endDate.toISOString() }, null, 2));

  // Lekérjük az ügyfél összes feladatát (nem csak a létrehozottakat!)
  let tasksQuery = query(
    collection(db, 'tasks'),
    where('customerId', '==', customerId),
    where('organizationId', '==', organizationId)
  );
  
  // Ha van projekt szűrő, hozzáadjuk
  if (projectId) {
    tasksQuery = query(tasksQuery, where('projectId', '==', projectId));
  }

  const tasksSnapshot = await getDocs(tasksQuery);
  console.log('[REPORT DEBUG] Total tasks fetched from Firestore:', JSON.stringify({ count: tasksSnapshot.docs.length }, null, 2));

  // Felhasználók lekérdezése a nevek kikereséséhez (ha assignedToName hiányzik)
  const usersSnapshot = await getDocs(query(
    collection(db, 'users'),
    where('organizationId', '==', organizationId)
  ));
  const userNames = new Map<string, string>();
  for (const userDoc of usersSnapshot.docs) {
    const userData = userDoc.data();
    userNames.set(userDoc.id, userData.displayName || userData.email || 'Ismeretlen');
  }
  console.log('[REPORT DEBUG] Users fetched for name lookup:', JSON.stringify({ count: userNames.size }, null, 2));

  // Időbejegyzések lekérdezése a pontos havi óraszámításhoz
  const { monthHours, hasTimeEntriesEver } = await fetchTimeEntryMaps(
    organizationId,
    startDate,
    endDate
  );
  console.log('[REPORT DEBUG] Time entries fetched:', JSON.stringify({ 
    tasksWithEntriesInMonth: monthHours.size,
    tasksWithEntriesEver: hasTimeEntriesEver.size 
  }, null, 2));
  
  const reportTasks: TaskReportData[] = [];
  let totalEstimatedHours = 0;
  let totalActualHours = 0;
  let completedTasksCount = 0;
  let skippedByStatus = 0;
  let skippedByDate = 0;

  // Feldolgozzuk a feladatokat
  for (const taskDoc of tasksSnapshot.docs) {
    const taskData = taskDoc.data();
    const taskId = taskDoc.id;
    
    // Státusz szűrés
    if (!statusFilter.includes(taskData.status)) {
      skippedByStatus++;
      console.log('[REPORT DEBUG] SKIPPED by status:', JSON.stringify({ taskNumber: taskData.taskNumber, status: taskData.status }, null, 2));
      continue;
    }
    
    // Ha dátum szűrés be van kapcsolva, ellenőrizzük
    if (filterByDate) {
      const inMonth = isTaskInMonth(taskData, startDate, endDate, dateFilterType);
      if (!inMonth) {
        skippedByDate++;
        console.log('[REPORT DEBUG] SKIPPED by date:', JSON.stringify({
          taskNumber: taskData.taskNumber,
          createdAt: taskData.createdAt?.toDate?.() ? taskData.createdAt.toDate().toISOString() : taskData.createdAt,
          completedDate: taskData.completedDate?.toDate?.() ? taskData.completedDate.toDate().toISOString() : taskData.completedDate,
          dueDate: taskData.dueDate?.toDate?.() ? taskData.dueDate.toDate().toISOString() : taskData.dueDate,
          updatedAt: taskData.updatedAt?.toDate?.() ? taskData.updatedAt.toDate().toISOString() : taskData.updatedAt
        }, null, 2));
        continue;
      }
    }

    console.log('[REPORT DEBUG] INCLUDED:', JSON.stringify({
      taskNumber: taskData.taskNumber,
      createdAt: taskData.createdAt?.toDate?.() ? taskData.createdAt.toDate().toISOString() : taskData.createdAt,
      completedDate: taskData.completedDate?.toDate?.() ? taskData.completedDate.toDate().toISOString() : taskData.completedDate,
      dueDate: taskData.dueDate?.toDate?.() ? taskData.dueDate.toDate().toISOString() : taskData.dueDate
    }, null, 2));

    // Pontos havi óraszámítás: időbejegyzések alapján, ha vannak
    const monthEntryHours = monthHours.get(taskId);
    const taskHasTimeEntries = hasTimeEntriesEver.has(taskId);
    const actualHours = taskHasTimeEntries
      ? (monthEntryHours || 0)
      : (taskData.actualHours || 0);
    const estimatedHours = taskData.estimatedHours || 0;

    console.log('[REPORT DEBUG] Task hours:', JSON.stringify({
      taskNumber: taskData.taskNumber,
      taskHasTimeEntries,
      monthEntryHours,
      taskActualHours: taskData.actualHours,
      calculatedActualHours: actualHours
    }, null, 2));

    reportTasks.push({
      taskNumber: taskData.taskNumber || 'N/A',
      title: taskData.title,
      status: taskData.status,
      priority: taskData.priority,
      assignedToName: taskData.assignedToName || userNames.get(taskData.assignedTo) || '-',
      estimatedHours,
      actualHours,
      completedDate: toDateSafe(taskData.completedDate),
      dueDate: toDateSafe(taskData.dueDate)
    });

    totalEstimatedHours += estimatedHours;
    totalActualHours += actualHours;
    
    if (taskData.status === 'completed') {
      completedTasksCount++;
    }
  }

  // Rendezzük a task-okat taskNumber alapján (TASK-001, TASK-002, ...)
  reportTasks.sort((a, b) => {
    const numA = parseInt(a.taskNumber.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.taskNumber.replace(/\D/g, '')) || 0;
    return numA - numB;
  });

  console.log('[REPORT DEBUG] SUMMARY:', JSON.stringify({
    totalFetched: tasksSnapshot.docs.length,
    included: reportTasks.length,
    skippedByStatus,
    skippedByDate,
    filterByDate,
    dateFilterType
  }, null, 2));

  return {
    customerId,
    customerName,
    month: `${year}-${month.toString().padStart(2, '0')}`,
    tasks: reportTasks,
    totalEstimatedHours,
    totalActualHours,
    completedTasksCount,
    totalTasksCount: reportTasks.length,
    generatedAt: new Date(),
    dateFilterType
  };
}

/**
 * Összes ügyfél havi kimutatása
 */
export async function generateAllCustomersMonthlyReport(
  year: number,
  month: number,
  organizationId: string,
  projectId?: string,
  filterByDate: boolean = true,
  statusFilter: string[] = ['todo', 'in_progress', 'completed', 'cancelled'],
  dateFilterType: DateFilterType = 'any'
): Promise<CustomerMonthlyReport[]> {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59);

  // Lekérjük az összes feladatot a szervezetből
  let tasksQuery = query(
    collection(db, 'tasks'),
    where('organizationId', '==', organizationId)
  );

  // Ha van projekt szűrő, hozzáadjuk
  if (projectId) {
    tasksQuery = query(tasksQuery, where('projectId', '==', projectId));
  }

  const tasksSnapshot = await getDocs(tasksQuery);

  // Felhasználók lekérdezése a nevek kikereséséhez (ha assignedToName hiányzik)
  const usersSnapshot = await getDocs(query(
    collection(db, 'users'),
    where('organizationId', '==', organizationId)
  ));
  const userNames = new Map<string, string>();
  for (const userDoc of usersSnapshot.docs) {
    const userData = userDoc.data();
    userNames.set(userDoc.id, userData.displayName || userData.email || 'Ismeretlen');
  }

  // Időbejegyzések lekérdezése a pontos havi óraszámításhoz
  const { monthHours, hasTimeEntriesEver } = await fetchTimeEntryMaps(
    organizationId,
    startDate,
    endDate
  );

  // Csoportosítás ügyfél szerint
  const customerTasksMap = new Map<string, { customerName: string; tasks: any[] }>();

  for (const taskDoc of tasksSnapshot.docs) {
    const taskData = taskDoc.data();
    const customerId = taskData.customerId;
    const customerName = taskData.customerName || 'Nincs ügyfél';

    if (!customerId) continue;

    // Státusz szűrés
    if (!statusFilter.includes(taskData.status)) {
      continue;
    }

    // Dátum szűrés
    if (filterByDate) {
      if (!isTaskInMonth(taskData, startDate, endDate, dateFilterType)) {
        continue;
      }
    }

    if (!customerTasksMap.has(customerId)) {
      customerTasksMap.set(customerId, { customerName, tasks: [] });
    }

    // taskDoc.id megőrzése az időbejegyzés lekérdezéshez
    customerTasksMap.get(customerId)!.tasks.push({ ...taskData, id: taskDoc.id });
  }

  // Generáljuk a riportokat minden ügyfélhez
  const reports: CustomerMonthlyReport[] = [];

  for (const [customerId, { customerName, tasks }] of Array.from(customerTasksMap.entries())) {
    const reportTasks: TaskReportData[] = [];
    let totalEstimatedHours = 0;
    let totalActualHours = 0;
    let completedTasksCount = 0;

    for (const task of tasks) {
      const taskId = task.id as string;
      const monthEntryHours = monthHours.get(taskId);
      const taskHasTimeEntries = hasTimeEntriesEver.has(taskId);
      const actualHours = taskHasTimeEntries
        ? (monthEntryHours || 0)
        : (task.actualHours || 0);
      const estimatedHours = task.estimatedHours || 0;

      reportTasks.push({
        taskNumber: task.taskNumber || 'N/A',
        title: task.title,
        status: task.status,
        priority: task.priority,
        assignedToName: task.assignedToName || userNames.get(task.assignedTo) || '-',
        estimatedHours,
        actualHours,
        completedDate: toDateSafe(task.completedDate),
        dueDate: toDateSafe(task.dueDate)
      });

      totalEstimatedHours += estimatedHours;
      totalActualHours += actualHours;

      if (task.status === 'completed') {
        completedTasksCount++;
      }
    }

    // Rendezzük a task-okat taskNumber alapján
    reportTasks.sort((a, b) => {
      const numA = parseInt(a.taskNumber.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.taskNumber.replace(/\D/g, '')) || 0;
      return numA - numB;
    });

    reports.push({
      customerId,
      customerName,
      month: `${year}-${month.toString().padStart(2, '0')}`,
      tasks: reportTasks,
      totalEstimatedHours,
      totalActualHours,
      completedTasksCount,
      totalTasksCount: reportTasks.length,
      generatedAt: new Date(),
      dateFilterType
    });
  }

  return reports;
}

/**
 * Riport exportálása szöveges formátumban
 */
export function exportReportAsText(report: CustomerMonthlyReport, aiReport?: string): string {
  const lines: string[] = [];
  
  lines.push('='.repeat(80));
  lines.push(`HAVI KIMUTATÁS - ${report.customerName}`);
  lines.push(`Időszak: ${report.month}`);
  lines.push(`Generálva: ${report.generatedAt.toLocaleString('hu-HU')}`);
  lines.push('='.repeat(80));
  lines.push('');
  
  // AI-generált report hozzáadása, ha van
  if (aiReport) {
    lines.push('AI-GENERÁLT TESZTELŐI REPORT:');
    lines.push('='.repeat(80));
    lines.push('');
    lines.push(aiReport);
    lines.push('');
    lines.push('='.repeat(80));
    lines.push('');
  }
  
  lines.push('ÖSSZESÍTÉS:');
  lines.push(`  Összes feladat: ${report.totalTasksCount} db`);
  lines.push(`  Befejezett feladatok: ${report.completedTasksCount} db`);
  lines.push(`  Becsült óraszám: ${report.totalEstimatedHours.toFixed(2)} óra`);
  lines.push(`  Tényleges óraszám: ${report.totalActualHours.toFixed(2)} óra`);
  lines.push(`  Eltérés: ${(report.totalActualHours - report.totalEstimatedHours).toFixed(2)} óra`);
  lines.push('');
  lines.push('-'.repeat(80));
  lines.push('');
  
  lines.push('FELADATOK RÉSZLETESEN:');
  lines.push('');
  
  report.tasks.forEach((task, index) => {
    lines.push(`${index + 1}. ${task.taskNumber} - ${task.title}`);
    lines.push(`   Státusz: ${getStatusLabel(task.status)}`);
    lines.push(`   Prioritás: ${getPriorityLabel(task.priority)}`);
    if (task.assignedToName) {
      lines.push(`   Felelős: ${task.assignedToName}`);
    }
    if (task.dueDate) {
      lines.push(`   Határidő: ${task.dueDate.toLocaleDateString('hu-HU')}`);
    }
    if (task.completedDate) {
      lines.push(`   Befejezve: ${task.completedDate.toLocaleDateString('hu-HU')}`);
    }
    lines.push(`   Becsült idő: ${task.estimatedHours?.toFixed(2) || '0.00'} óra`);
    lines.push(`   Tényleges idő: ${task.actualHours.toFixed(2)} óra`);
    lines.push('');
  });
  
  lines.push('='.repeat(80));
  
  return lines.join('\n');
}

/**
 * Riport exportálása CSV formátumban
 */
export function exportReportAsCSV(report: CustomerMonthlyReport): string {
  const lines: string[] = [];
  
  // Header
  lines.push(`"Ügyfél","${report.customerName}"`);
  lines.push(`"Időszak","${report.month}"`);
  lines.push(`"Generálva","${report.generatedAt.toLocaleString('hu-HU')}"`);
  lines.push('');
  
  // Összesítés
  lines.push('"ÖSSZESÍTÉS"');
  lines.push(`"Összes feladat","${report.totalTasksCount}"`);
  lines.push(`"Befejezett feladatok","${report.completedTasksCount}"`);
  lines.push(`"Becsült óraszám","${report.totalEstimatedHours.toFixed(2)}"`);
  lines.push(`"Tényleges óraszám","${report.totalActualHours.toFixed(2)}"`);
  lines.push(`"Eltérés","${(report.totalActualHours - report.totalEstimatedHours).toFixed(2)}"`);
  lines.push('');
  
  // Feladatok táblázat
  lines.push('"Feladat szám","Cím","Státusz","Prioritás","Felelős","Határidő","Befejezve","Becsült óra","Tényleges óra"');
  
  report.tasks.forEach(task => {
    const row = [
      `"${task.taskNumber}"`,
      `"${task.title}"`,
      `"${getStatusLabel(task.status)}"`,
      `"${getPriorityLabel(task.priority)}"`,
      `"${task.assignedToName || ''}"`,
      `"${task.dueDate ? task.dueDate.toLocaleDateString('hu-HU') : ''}"`,
      `"${task.completedDate ? task.completedDate.toLocaleDateString('hu-HU') : ''}"`,
      `"${task.estimatedHours?.toFixed(2) || '0.00'}"`,
      `"${task.actualHours.toFixed(2)}"`
    ];
    lines.push(row.join(','));
  });
  
  return lines.join('\n');
}

// Helper függvények
function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    todo: 'Tennivaló',
    in_progress: 'Folyamatban',
    completed: 'Befejezett',
    cancelled: 'Törölve'
  };
  return labels[status] || status;
}

function getPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    low: 'Alacsony',
    medium: 'Közepes',
    high: 'Magas',
    urgent: 'Sürgős'
  };
  return labels[priority] || priority;
}

/**
 * Riport exportálása PDF formátumban (pdfmake - teljes UTF-8 támogatással)
 */
export function exportReportAsPDF(
  report: CustomerMonthlyReport,
  showColumns: 'both' | 'estimated' | 'actual' = 'actual',
  aiReport?: string
): void {
  // Táblázat adatok előkészítése - oszlopok szerint
  const tableBody = report.tasks.map(task => {
    const row = [
      task.taskNumber,
      { text: task.title, noWrap: false }, // Teljes szöveg, többsoros
      getStatusLabel(task.status),
      getPriorityLabel(task.priority),
      task.assignedToName || '-'
    ];
    
    if (showColumns === 'both') {
      row.push(task.estimatedHours?.toFixed(1) || '0.0');
      row.push(task.actualHours.toFixed(1));
    } else if (showColumns === 'estimated') {
      row.push(task.estimatedHours?.toFixed(1) || '0.0');
    } else {
      row.push(task.actualHours.toFixed(1));
    }
    
    return row;
  });

  // Összesítő sor hozzáadása
  const summaryRow: any[] = [
    { text: 'ÖSSZESEN', bold: true, colSpan: 5, alignment: 'right' },
    {},
    {},
    {},
    {}
  ];
  
  if (showColumns === 'both') {
    summaryRow.push({ text: report.totalEstimatedHours.toFixed(1), bold: true });
    summaryRow.push({ text: report.totalActualHours.toFixed(1), bold: true });
  } else if (showColumns === 'estimated') {
    summaryRow.push({ text: report.totalEstimatedHours.toFixed(1), bold: true });
  } else {
    summaryRow.push({ text: report.totalActualHours.toFixed(1), bold: true });
  }
  
  tableBody.push(summaryRow);

  // PDF dokumentum definíció
  const docDefinition: any = {
    content: [
      // Fejléc
      { text: 'HAVI KIMUTATÁS', style: 'header' },
      { text: '\n' },
      { text: `Ügyfél: ${report.customerName}`, style: 'subheader' },
      { text: `Időszak: ${report.month}` },
      { text: `Generálva: ${report.generatedAt.toLocaleString('hu-HU')}` },
      { text: '\n' },
      
      // AI-generált report, ha van
      ...(aiReport ? [
        { text: 'AI-GENERÁLT TESZTELŐI REPORT', style: 'sectionHeader' },
        { text: '\n' },
        { 
          text: aiReport,
          style: 'aiReport',
          margin: [10, 0, 10, 10]
        },
        { text: '\n' },
        { text: '\n' }
      ] : []),
      
      // Összesítés
      { text: 'ÖSSZESÍTÉS', style: 'sectionHeader' },
      {
        ul: (() => {
          const items = [
            `Összes feladat: ${report.totalTasksCount} db`,
            `Befejezett feladatok: ${report.completedTasksCount} db`
          ];
          
          if (showColumns === 'both') {
            items.push(`Becsült óraszám: ${report.totalEstimatedHours.toFixed(2)} óra`);
            items.push(`Tényleges óraszám: ${report.totalActualHours.toFixed(2)} óra`);
            items.push(`Eltérés: ${(report.totalActualHours - report.totalEstimatedHours).toFixed(2)} óra`);
          } else if (showColumns === 'estimated') {
            items.push(`Becsült óraszám: ${report.totalEstimatedHours.toFixed(2)} óra`);
          } else {
            items.push(`Tényleges óraszám: ${report.totalActualHours.toFixed(2)} óra`);
          }
          
          return items;
        })()
      },
      { text: '\n' },
      
      // Feladatok táblázat
      { text: 'FELADATOK RÉSZLETESEN', style: 'sectionHeader' },
      { text: '\n' },
      {
        table: {
          headerRows: 1,
          widths: showColumns === 'both' 
            ? ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto']
            : ['auto', '*', 'auto', 'auto', 'auto', 'auto'],
          body: [
            (() => {
              const headers = [
                { text: 'Szám', style: 'tableHeader' },
                { text: 'Cím', style: 'tableHeader' },
                { text: 'Státusz', style: 'tableHeader' },
                { text: 'Prioritás', style: 'tableHeader' },
                { text: 'Felelős', style: 'tableHeader' }
              ];
              
              if (showColumns === 'both') {
                headers.push({ text: 'Becsült (h)', style: 'tableHeader' });
                headers.push({ text: 'Tényleges (h)', style: 'tableHeader' });
              } else if (showColumns === 'estimated') {
                headers.push({ text: 'Becsült (h)', style: 'tableHeader' });
              } else {
                headers.push({ text: 'Tényleges (h)', style: 'tableHeader' });
              }
              
              return headers;
            })(),
            ...tableBody
          ]
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? '#428bca' : (rowIndex % 2 === 0 ? '#f9f9f9' : null))
        }
      },
      
      // Lábjegyzet
      { text: '\n\n' },
      { 
        text: 'Készült a YouCRM rendszerrel', 
        style: 'footer',
        alignment: 'center'
      }
    ],
    styles: {
      header: {
        fontSize: 14,
        bold: true,
        alignment: 'center'
      },
      logo: {
        fontSize: 16,
        bold: true,
        color: '#000000'
      },
      subheader: {
        fontSize: 11,
        bold: true
      },
      sectionHeader: {
        fontSize: 10,
        bold: true,
        margin: [0, 8, 0, 4]
      },
      tableHeader: {
        bold: true,
        fontSize: 8,
        color: 'white'
      },
      aiReport: {
        fontSize: 8,
        lineHeight: 1.2,
        italics: false
      },
      footer: {
        fontSize: 7,
        italics: true,
        color: '#AAAAAA'
      }
    },
    defaultStyle: {
      fontSize: 8
    }
  };

  // PDF generálás és letöltés
  const fileName = `riport_${report.customerName.replace(/[^a-z0-9]/gi, '_')}_${report.month}.pdf`;
  pdfMake.createPdf(docDefinition).download(fileName);
}
